import { action, observable, makeObservable } from 'mobx';
import { type TMessage, Translate } from '../../i18n';
import { m } from '../../mithril';
import { Services } from '../../services';
import { addStyles } from '../theme';

const CSS = {
	container: 'c-Notification-Toast',
	text: 'mdl-snackbar__text',
	button: 'mdl-snackbar__action',
	message: 'dgn-notification__message',
	isActive: 'mdl-snackbar--active',
	error: 'c-Notification-Toast--error',
	success: 'c-Notification-Toast--success',
	warn: 'c-Notification-Toast--warn',

	MDL: {
		container: 'dgn-notification mdl-snackbar',
	},
};

addStyles({
	[CSS.container]: {
		'z-index': 999,
		'border-radius': '4px',
	},
	[CSS.error]: {
		'border-left': '4px solid #d32f2f',
		background: '#424242',
	},
	[CSS.success]: {
		'border-left': '4px solid #4caf50',
		background: '#424242',
	},
	[CSS.warn]: {
		'border-left': '4px solid #ff9800',
		background: '#424242',
	},
});

export enum NotificationToastLevel {
	SUCCESS,
	INFO,
	WARN,
	ERROR,
}

const LEVEL_CSS_CLASS: Record<NotificationToastLevel, string> = {
	[NotificationToastLevel.ERROR]: CSS.error,
	[NotificationToastLevel.SUCCESS]: CSS.success,
	[NotificationToastLevel.WARN]: CSS.warn,
	[NotificationToastLevel.INFO]: '',
};

const TOAST_TIMEOUT_MS = 3500;

export class NotificationServiceImpl {
	@observable message: TMessage | null = null;
	@observable messageString: string | null = null;
	@observable isOpen: boolean = false;
	@observable level: NotificationToastLevel = NotificationToastLevel.INFO;

	private timeout = null;

	constructor() {
		makeObservable(this);
	}

	success(message: TMessage) {
		this.message = message;
		this.level = NotificationToastLevel.SUCCESS;
		this.show();
	}

	info(message: TMessage) {
		this.message = message;
		this.level = NotificationToastLevel.INFO;
		this.show();
	}

	warn(message: TMessage) {
		this.message = message;
		this.level = NotificationToastLevel.WARN;
		this.show();
	}

	error(message: TMessage) {
		this.message = message;
		this.level = NotificationToastLevel.ERROR;
		this.show();
	}

	errorAsString(message: string) {
		this.messageString = message;
		this.level = NotificationToastLevel.ERROR;
		this.show();
	}

	view() {
		const className = [CSS.container, CSS.MDL.container, LEVEL_CSS_CLASS[this.level], this.isOpen ? CSS.isActive : null]
			.filter(Boolean)
			.join(' ');

		var messageText = '';
		if (this.messageString) {
			messageText = this.messageString;
		} else if (this.message) {
			messageText = Translate(this.message.key, this.message.attrs);
		}

		return m('div', { className }, [m('div', { className: CSS.text }, messageText)]);
	}

	@action show() {
		if (this.timeout != null) {
			Services.Window.window.clearTimeout(this.timeout);
		}
		this.timeout = Services.Window.window.setTimeout((_) => this.hide(), TOAST_TIMEOUT_MS);
		this.isOpen = true;
	}

	@action hide() {
		if (this.timeout != null) {
			Services.Window.window.clearTimeout(this.timeout);
			this.timeout = null;
		}
		this.isOpen = false;
	}
}

export const NotificationService = new NotificationServiceImpl();
